import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Megaphone, Trash2, Pencil, Save, X, Image as ImageIcon,
         Sparkles, Eye, Calendar, Upload, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminOffers() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => { if (!loading && !isStaff) navigate("/login"); }, [user, loading, navigate, isStaff]);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/offers"); setOffers(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [user]);

  const openNew = () => setEditing({ _new: true, title: "", description: "", html: "", images: [],
                                     valid_from: todayISO(), valid_until: plusDaysISO(14),
                                     cta_label: "Richiedi informazioni", is_published: false });
  const openEdit = (o) => setEditing({ ...o });
  const closeEditor = () => setEditing(null);

  const togglePublish = async (o) => {
    try { await api.patch(`/admin/offers/${o.offer_id}`, { is_published: !o.is_published });
          toast.success(o.is_published ? "Offerta disattivata" : "Offerta pubblicata"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const removeOffer = async (o) => {
    if (!window.confirm(`Eliminare "${o.title}"?`)) return;
    try { await api.delete(`/admin/offers/${o.offer_id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!isStaff) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-5xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Marketing</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7"/> Offerte pubbliche
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Crea popup promozionali con validità temporale. Visibili a tutti i visitatori non staff.
              Puoi scrivere l'HTML a mano o farti generare la "locandina" dall'IA.
            </p>
          </div>
          <Button onClick={openNew} className="bg-blue-800 hover:bg-blue-900" data-testid="offer-new-btn">
            <Plus className="w-4 h-4 mr-2"/> Nuova offerta
          </Button>
        </div>

        {busy && offers.length === 0 ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>
        ) : offers.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
            <p className="text-slate-600">Nessuna offerta ancora creata.</p>
            <Button onClick={openNew} className="mt-4 bg-blue-800">Crea la prima offerta</Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="offers-list">
            {offers.map(o => (
              <Card key={o.offer_id} className="p-4" data-testid={`offer-${o.offer_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-900 truncate">{o.title}</h3>
                      {o.is_active_now ? (
                        <span className="text-[10px] bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded uppercase font-semibold">● Attiva ora</span>
                      ) : o.is_published ? (
                        <span className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded uppercase font-semibold">Pubblicata ma fuori date</span>
                      ) : (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded uppercase font-semibold">Bozza</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{o.description || <i>Nessuna descrizione</i>}</p>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/> {o.valid_from || "—"} → {o.valid_until || "—"}</span>
                      {o.images?.length > 0 && <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3"/> {o.images.length} immagini</span>}
                      {o.html && <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> HTML personalizzato</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                      <Switch checked={!!o.is_published} onCheckedChange={() => togglePublish(o)} data-testid={`offer-pub-${o.offer_id}`}/>
                      Pubblicata
                    </label>
                    <button onClick={() => setPreviewing(o)} className="p-1.5 text-slate-500 hover:text-blue-800" title="Anteprima" data-testid={`offer-prev-${o.offer_id}`}>
                      <Eye className="w-4 h-4"/>
                    </button>
                    <button onClick={() => openEdit(o)} className="p-1.5 text-slate-500 hover:text-blue-800" title="Modifica" data-testid={`offer-edit-${o.offer_id}`}>
                      <Pencil className="w-4 h-4"/>
                    </button>
                    <button onClick={() => removeOffer(o)} className="p-1.5 text-slate-500 hover:text-red-600" title="Elimina" data-testid={`offer-del-${o.offer_id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <OfferEditor initial={editing} onClose={closeEditor} onSaved={() => { closeEditor(); load(); }}/>
      )}
      {previewing && (
        <OfferPreviewDialog offer={previewing} onClose={() => setPreviewing(null)}/>
      )}
    </div>
  );
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
function plusDaysISO(d) { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }

function OfferEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiHint, setAiHint] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef(null);
  const isNew = !!initial._new;

  const save = async () => {
    if (!f.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    if (!f.valid_from || !f.valid_until) { toast.error("Date di validità obbligatorie"); return; }
    if (f.valid_from > f.valid_until) { toast.error("La data 'dal' deve essere prima di 'al'"); return; }
    setBusy(true);
    try {
      const payload = {
        title: f.title.trim(), description: f.description || "",
        html: f.html || "", images: f.images || [],
        valid_from: f.valid_from, valid_until: f.valid_until,
        cta_label: f.cta_label || "Richiedi informazioni",
        is_published: !!f.is_published,
      };
      if (isNew) {
        const r = await api.post("/admin/offers", payload);
        setF({ ...r.data, _new: false });
        toast.success("Offerta creata");
      } else {
        await api.patch(`/admin/offers/${f.offer_id}`, payload);
        toast.success("Modifiche salvate");
      }
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const upload = async (filesList) => {
    const arr = Array.from(filesList || []);
    if (!arr.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of arr) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} > 15MB`); continue; }
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      setF(x => ({ ...x, images: [...(x.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} immagini caricate`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const removeImage = (idx) => setF(x => ({ ...x, images: x.images.filter((_, i) => i !== idx) }));

  const generateHtml = async () => {
    if (isNew) { toast.error("Salva prima l'offerta, poi genera l'HTML"); return; }
    setAiBusy(true);
    try {
      const r = await api.post(`/admin/offers/${f.offer_id}/generate-html`, { extra_hint: aiHint });
      setF(x => ({ ...x, html: r.data.html }));
      toast.success("Locandina IA generata — rivedila nel campo HTML");
    } catch (e) { toast.error(formatErr(e)); } finally { setAiBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="offer-editor">
        <DialogHeader className="px-5 py-3 border-b bg-gradient-to-r from-blue-800 to-indigo-600 rounded-t-lg">
          <DialogTitle className="text-white flex items-center gap-2 heading text-xl">
            <Megaphone className="w-5 h-5"/>
            {isNew ? "Nuova offerta pubblica" : "Modifica offerta"}
          </DialogTitle>
        </DialogHeader>
        <div className="p-5 space-y-4">
          <div>
            <L>Nome offerta *</L>
            <Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })}
                   placeholder="Es: Sconto 20% sui condizionatori" data-testid="offer-title"/>
          </div>
          <div>
            <L>Descrizione (visibile solo se NON usi HTML personalizzato)</L>
            <textarea className="w-full min-h-[80px] rounded-md border border-slate-300 p-2 text-sm"
                      value={f.description || ""} onChange={e => setF({ ...f, description: e.target.value })}
                      placeholder="Breve descrizione dell'offerta…"
                      data-testid="offer-description"/>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><L>Valida dal *</L>
              <Input type="date" value={f.valid_from || ""} onChange={e => setF({ ...f, valid_from: e.target.value })} data-testid="offer-from"/></div>
            <div><L>Valida al *</L>
              <Input type="date" value={f.valid_until || ""} onChange={e => setF({ ...f, valid_until: e.target.value })} data-testid="offer-to"/></div>
            <div><L>Testo bottone CTA</L>
              <Input value={f.cta_label || ""} onChange={e => setF({ ...f, cta_label: e.target.value })}
                     placeholder="Richiedi informazioni" data-testid="offer-cta"/></div>
          </div>

          <div>
            <L>Immagini (max 10, 15MB cad.)</L>
            <div className="flex flex-wrap gap-2 items-start">
              {(f.images || []).map((im, i) => (
                <div key={i} className="relative w-24 h-24 rounded border bg-slate-100 overflow-hidden group">
                  {f.offer_id ? (
                    <img src={`${BACKEND}/api/offers/public/image/${f.offer_id}/${i}`}
                         alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs text-center p-1">{im.original_filename}</div>
                  )}
                  <button onClick={() => removeImage(i)}
                          className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                          data-testid={`offer-img-rm-${i}`}>
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              ))}
              <button onClick={() => fileRef.current?.click()}
                      className="w-24 h-24 rounded border-2 border-dashed border-slate-300 hover:border-blue-500 flex flex-col items-center justify-center text-slate-500 hover:text-blue-700 text-xs"
                      data-testid="offer-img-add">
                <Upload className="w-5 h-5 mb-1"/> Carica
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden
                     onChange={e => { upload(e.target.files); e.target.value = ""; }}/>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-amber-900 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5"/> Locandina HTML (opzionale)</span>
              {!isNew && (
                <Button size="sm" onClick={generateHtml} disabled={aiBusy}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="offer-ai-gen">
                  {aiBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Sparkles className="w-4 h-4 mr-1"/> Chiedi all'IA</>}
                </Button>
              )}
            </div>
            <Input placeholder="Suggerimento extra per l'IA (es: stile elegante serale, enfasi sullo sconto…)"
                   value={aiHint} onChange={e => setAiHint(e.target.value)}
                   className="mb-2 text-xs" data-testid="offer-ai-hint"/>
            <textarea className="w-full min-h-[200px] font-mono text-xs rounded-md border border-amber-300 bg-white p-2"
                      value={f.html || ""} onChange={e => setF({ ...f, html: e.target.value })}
                      placeholder="Lascia vuoto per usare un layout automatico dalla descrizione, oppure incolla/edita qui l'HTML con classi Tailwind."
                      data-testid="offer-html"/>
            {f.html && (
              <Button size="sm" variant="outline" onClick={() => setShowPreview(true)}
                      className="mt-2" data-testid="offer-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-1"/> Anteprima locandina
              </Button>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer p-2 rounded border border-slate-200">
            <Switch checked={!!f.is_published} onCheckedChange={v => setF({ ...f, is_published: v })} data-testid="offer-publish-switch"/>
            <span className="text-sm font-medium text-slate-800">Pubblica subito (visibile a tutti i visitatori nel periodo di validità)</span>
          </label>
        </div>
        <div className="flex gap-2 justify-end px-5 py-3 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-800" data-testid="offer-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1"/> Salva</>}
          </Button>
        </div>
      </DialogContent>
      {showPreview && <OfferPreviewDialog offer={f} onClose={() => setShowPreview(false)}/>}
    </Dialog>
  );
}

function OfferPreviewDialog({ offer, onClose }) {
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 bg-white overflow-hidden" data-testid="offer-preview">
        <div className="relative">
          <button onClick={onClose} className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center">
            <X className="w-5 h-5"/>
          </button>
          <OfferContent offer={offer}/>
          <div className="p-4 border-t bg-slate-50 text-center">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold">
              {offer.cta_label || "Richiedi informazioni"} (anteprima)
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OfferContent({ offer }) {
  if (offer.html && offer.html.trim()) {
    return <div className="offer-html" dangerouslySetInnerHTML={{ __html: offer.html }}/>;
  }
  // Default layout when no custom HTML
  const hasImg = (offer.images || []).length > 0 && offer.offer_id;
  return (
    <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-700 text-white p-6">
      {hasImg && (
        <img src={`${BACKEND}/api/offers/public/image/${offer.offer_id}/0`}
             alt={offer.title} className="w-full max-h-56 object-cover rounded-lg mb-4 shadow-lg"/>
      )}
      <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-1">Offerta limitata</div>
      <h2 className="heading text-3xl font-extrabold mb-2">{offer.title}</h2>
      <p className="text-blue-100 text-sm leading-relaxed whitespace-pre-line">{offer.description || ""}</p>
      <div className="mt-4 inline-block bg-amber-500 text-blue-950 text-xs font-bold px-2 py-1 rounded">
        Valida dal {fmtDate(offer.valid_from)} al {fmtDate(offer.valid_until)}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { const d = new Date(iso); return d.toLocaleDateString("it-IT"); } catch { return iso; }
}

function L({ children }) {
  return <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{children}</label>;
}
